import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Columns2, Download, RefreshCcw, Send } from "lucide-react";
import { toast } from "sonner";

function CompareImg({ taskId, id }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/cleaning-tasks/${taskId}/photos/${id}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [taskId, id]);
  return src
    ? <a href={src} target="_blank" rel="noreferrer" className="shrink-0"><img src={src} alt="" className="w-28 h-28 rounded-lg object-cover" data-testid={`compare-photo-${id}`} /></a>
    : <div className="w-28 h-28 rounded-lg bg-slate-100 animate-pulse shrink-0" />;
}

const FLAG_TONE = { changed: "bg-rose-50 border-rose-200 text-rose-800", missing: "bg-rose-50 border-rose-200 text-rose-800",
  ok: "bg-emerald-50 border-emerald-100 text-emerald-800", no_baseline: "bg-slate-50 border-slate-200 text-slate-600", error: "bg-amber-50 border-amber-100 text-amber-800" };
const FLAG_LABEL = { changed: "Changed", missing: "Missing", ok: "Looks the same", no_baseline: "No baseline", error: "Check failed" };

export function RoomFlag({ check, testId }) {
  if (!check) return null;
  return (
    <div className={`mt-1 text-[11px] rounded-md border px-2 py-1 ${FLAG_TONE[check.status]}`} data-testid={testId}>
      <span className="font-medium inline-flex items-center gap-1">{(check.status === "changed" || check.status === "missing") && <AlertTriangle className="w-3 h-3" />}{FLAG_LABEL[check.status]}</span>
      {check.issues?.length > 0 && <div>{check.issues.join(" · ")}</div>}
    </div>
  );
}

function Side({ side, room }) {
  const photos = side?.photos?.[room] || [];
  if (!photos.length) return <div className="h-28 rounded-lg border border-dashed border-slate-200 flex items-center justify-center text-xs text-slate-400 flex-1">No photo</div>;
  return <div className="flex gap-2 overflow-x-auto flex-1">{photos.map((p) => <CompareImg key={p.id} taskId={side.task_id} id={p.id} />)}</div>;
}

export function PhotoCompareDialog({ taskId, title, onClose }) {
  const [d, setD] = useState(null);
  const [running, setRunning] = useState(false);
  const load = () => api.get(`/cleaning-tasks/${taskId}/photo-compare`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [taskId]);
  const rerun = async () => {
    setRunning(true);
    try { const { data } = await api.post(`/cleaning-tasks/${taskId}/room-check`); toast.success(data.flagged.length ? `${data.flagged.length} room(s) flagged` : "No changes spotted"); load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setRunning(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="photo-compare-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-serif">Compare with last turnover{title ? ` · ${title}` : ""}</DialogTitle></DialogHeader>
        {!d ? <div className="text-sm text-slate-400">Loading…</div> : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500" data-testid="compare-check-summary">
              <span>{d.check ? `AI room check ${d.check.checked_at.slice(0, 16).replace("T", " ")} · ${d.check.flagged.length} room(s) flagged` : "AI room check not run yet"}</span>
              <Button size="sm" variant="outline" className="rounded-full" disabled={running} onClick={rerun} data-testid="compare-run-check">
                <RefreshCcw className={`w-3.5 h-3.5 mr-1.5 ${running ? "animate-spin" : ""}`} /> {running ? "Checking…" : d.check ? "Re-run AI check" : "Run AI check"}
              </Button>
            </div>
            <div className="grid grid-cols-[8rem_1fr_1fr] gap-4 text-[11px] uppercase tracking-wider text-slate-500 pb-2 border-b border-slate-100">
              <span>Room</span>
              <span data-testid="compare-previous-label">{d.previous ? `Previous · ${d.previous.date}${d.previous.cleaner ? ` · ${d.previous.cleaner}` : ""}` : "No earlier turnover with photos"}</span>
              <span data-testid="compare-current-label">This turnover · {d.current.date}{d.current.cleaner ? ` · ${d.current.cleaner}` : ""}</span>
            </div>
            {d.rooms.map((room, i) => (
              <div key={room} className="grid grid-cols-[8rem_1fr_1fr] gap-4 py-3 border-b border-slate-100 items-center" data-testid={`compare-room-${i}`}>
                <div className="text-sm">{room}<RoomFlag check={d.check?.rooms?.[room]} testId={`compare-flag-${i}`} /></div>
                <Side side={d.previous} room={room} />
                <Side side={d.current} room={room} />
              </div>
            ))}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CompareButton({ taskId, title, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid={testId}>
        <Columns2 className="w-3.5 h-3.5 mr-1.5" /> Compare with last turnover
      </Button>
      {open && <PhotoCompareDialog taskId={taskId} title={title} onClose={() => setOpen(false)} />}
    </>
  );
}

export function InvoiceActions({ r, onSent }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    try {
      const res = await api.get(`/damage-reports/${r.id}/invoice.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) { toast.error(apiError(err)); }
  };
  const resend = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/damage-reports/${r.id}/invoice/send`); data.ok ? toast.success(`Invoice sent to ${data.to}`) : toast.error("Invoice email failed"); onSent(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  const last = r.invoice_last_sent;
  const payState = r.paid_at ? "paid" : r.charge?.method === "manual" && r.pay_url ? "unpaid" : null;
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`damage-invoice-${r.id}`}>
      <button onClick={download} data-testid={`damage-invoice-download-${r.id}`} className="text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border border-slate-200 hover:border-teal-600 hover:text-teal-700 transition-colors inline-flex items-center gap-1"><Download className="w-3 h-3" /> Invoice PDF</button>
      <button onClick={resend} disabled={busy} data-testid={`damage-invoice-resend-${r.id}`} className="text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border border-slate-200 hover:border-teal-600 hover:text-teal-700 transition-colors inline-flex items-center gap-1 disabled:opacity-50"><Send className="w-3 h-3" /> {last ? "Resend" : "Send"} invoice</button>
      <span className="text-[11px] text-slate-500" data-testid={`damage-invoice-status-${r.id}`}>
        {last ? `${r.invoice_number} · ${last.ok ? "sent" : "failed"} ${last.sent_at.slice(0, 10)} to ${last.to}` : "Invoice not sent yet"}
      </span>
      {payState === "unpaid" && r.payment_reminders?.length > 0 && (
        <span className="text-[11px] text-slate-500" data-testid={`damage-reminders-${r.id}`}>
          {r.payment_reminders.length} reminder(s) sent · last {r.payment_reminders[r.payment_reminders.length - 1].sent_at.slice(0, 10)}{r.payment_reminders.some((x) => x.final) ? " (final)" : ""}
        </span>
      )}
      {payState === "paid" && <span className="text-[10px] uppercase tracking-widest bg-emerald-600 text-white rounded px-1.5 py-0.5" data-testid={`damage-paid-badge-${r.id}`}>Paid {r.paid_at.slice(0, 10)}</span>}
      {payState === "unpaid" && (
        <button onClick={() => { navigator.clipboard.writeText(r.pay_url); toast.success("Pay link copied"); }} data-testid={`damage-pay-link-${r.id}`}
          className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 hover:bg-amber-200">Awaiting payment · copy link</button>
      )}
    </div>
  );
}

export function DepositBoostCard() {
  const [m, setM] = useState("");
  useEffect(() => { api.get("/settings/damage-deposit-boost").then((r) => setM(r.data.multiplier)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/settings/damage-deposit-boost", { multiplier: Number(m) }); toast.success("Saved"); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="ns-card p-8 mt-6" data-testid="settings-deposit-boost">
      <div className="font-serif text-2xl">Rebooking deposit boost</div>
      <p className="text-sm text-slate-500 mt-1 mb-4">Guests with previous damage reports get their deposit multiplied by this when a guest journey is created. Set 1 to switch off.</p>
      <div className="flex gap-2 items-center">
        <Input type="number" min={1} max={10} step="0.5" className="w-28" value={m} onChange={(e) => setM(e.target.value)} data-testid="settings-deposit-boost-multiplier" />
        <span className="text-sm text-slate-500">× normal deposit</span>
        <Button className="rounded-full ns-btn-primary" onClick={save} data-testid="settings-deposit-boost-save">Save</Button>
      </div>
    </div>
  );
}

export function CareTipsCard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/settings/house-care-tips").then((r) => setD({ ...r.data, text: r.data.tips.join("\n") })).catch(() => {}); }, []);
  if (!d) return null;
  const save = async () => {
    try { await api.put("/settings/house-care-tips", { title: d.title, intro: d.intro, tips: d.text.split("\n") }); toast.success("Saved"); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="ns-card p-8 mt-6" data-testid="settings-care-tips">
      <div className="font-serif text-2xl">House-care tips</div>
      <p className="text-sm text-slate-500 mt-1 mb-4">Shown gently on the guest journey page to guests with previous damage reports. Past damage is never mentioned.</p>
      <Input className="mb-2" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} data-testid="settings-care-tips-title" />
      <Textarea rows={2} className="mb-2" value={d.intro} onChange={(e) => setD({ ...d, intro: e.target.value })} data-testid="settings-care-tips-intro" />
      <Textarea rows={6} value={d.text} onChange={(e) => setD({ ...d, text: e.target.value })} placeholder="One tip per line" data-testid="settings-care-tips-list" />
      <Button className="rounded-full ns-btn-primary mt-3" onClick={save} data-testid="settings-care-tips-save">Save</Button>
    </div>
  );
}
